import { gql } from "@apollo/client";

export const CHECK_FEATURE_ACCESS = gql`
  query checkFeatureAccess($feature: String) {
    checkFeatureAccess(feature: $feature)
  }
`;

const PROFILE_CARD_USER_FIELDS = gql`
  fragment ProfileCardUserFragment on User {
    _id
    username
    bio
    avatarUrl
    coverImageUrl
    followerCount
    followingCount
    createdDate
    userGroup {
      type
      active
    }
    rank
    sharedFollows {
      _id
      username
      avatarUrl
      followerCount
      followingCount
      userGroup {
        type
        active
      }
    }
    profile {
      profile {
        countryCode
        socials {
          youtube
          twitch
          tiktok
          twitter
          instagram
        }
      }
    }
    discord {
      username
    }
    riot {
      riotId
      puuid
      active
    }
    steam {
      personaname
      steamid
      active
    }
    epic {
      displayName
      id
    }
    battlenet {
      battletag
    }
    teams {
      nodes {
        id
        name
        slug
        tag
        tagImageUrl
      }
    }
  }
`;

const USER_FIELDS = gql`
  fragment UserFragment on User {
    teams {
      nodes {
        id
        name
        tagImageUrl
        tag
        slug
      }
    }
    montageCount
    followerCount
    followingCount
    followers {
      nodes {
        _id
        username
        avatarUrl
      }
    }
    following {
      nodes {
        _id
        username
        avatarUrl
      }
    }
    _id
    createdDate
    sharedFollows {
      username
      avatarUrl
      userGroup {
        type
      }
    }
    profile {
      profile {
        countryCode
        socials {
          youtube
          twitch
          tiktok
          twitter
          instagram
        }
      }
    }
    pinnedClips {
      createdAt
      id
      isPinned
      internalId
      title
      type
      views
      thumbnailUrl(style: STANDARD)
      url(orientation: LANDSCAPE)
      shareId
      reactions {
        count
        type
      }
      user {
        _id
      }
      moment {
        id
        match {
          outdated
        }
      }
      game
    }
    discord {
      username
    }
    riot {
      riotId
      puuid
      active
    }
    steam {
      personaname
      steamid
      active
    }
    epic {
      displayName
      id
    }
    battlenet {
      battletag
    }
    userGroup {
      type
      active
    }
    rank
    playerGameIds
    matches {
      thumbnail
      player {
        gameId
      }
      title
      matchPlayedAt
      result
      killCount
      deathCount
      momentCount
      assistCount
      role
      game
      score
      id
      moments(limit: 3, sort: SCORE) {
        id
        title
        timelineLabel
        clip {
          status
          internalId
        }
        player {
          avatar
          displayName
          gameId
          team
        }
        displayTags {
          value
          key
        }
        victimData {
          team
          gameId
          displayName
          avatar
        }
        displayTags {
          value
          key
        }
        icons
        score
        victimCount
      }
    }
  }
`;

export const GET_PROFILE_CARD_DATA = gql`
  ${PROFILE_CARD_USER_FIELDS}

  query getProfileCardData($username: String!) {
    user(username: $username) {
      ...ProfileCardUserFragment
    }
  }
`;

export const GET_USER_PROFILE = gql`
  ${USER_FIELDS}

  query getUserProfile($query: String!) {
    profile(query: $query) {
      __typename
      username
      avatarUrl
      bio
      coverImageUrl
      clipCount
      followerCount
      followingCount
      playedGames

      ... on User {
        ...UserFragment
      }
    }
  }
`;

export const GET_USER_CLIPS = gql`
  query getUserClips(
    $query: String!
    $first: Int!
    $after: String
    $filters: ClipFilter
    $sort: ClipSort
    $statuses: [ClipStatus!]
    $orientation: MediaOrientation!
    $thumbnailStyle: ThumbnailStyle!
  ) {
    profile(query: $query) {
      clips(
        sort: $sort
        statuses: $statuses
        first: $first
        after: $after
        filters: $filters
        orientation: $orientation
      ) {
        pageInfo {
          endCursor
          hasNextPage
          totalCount
          startCursor
          hasPreviousPage
        }
        nodes {
          createdAt
          game
          id
          internalId
          trackId
          isPinned
          previewUrl
          playerGameIdentifier
          thumbnailUrl(style: $thumbnailStyle)
          title
          shareId
          status
          type
          url(orientation: $orientation)
          views
          reactions {
            count
            type
          }
          moment {
            id
            match {
              outdated
            }
          }
          user {
            _id
          }
        }
      }
    }
  }
`;

export const GET_USER_MONTAGES = gql`
  query getUserMontages(
    $query: String!
    $first: Int!
    $after: String
    $status: ClipStatus
  ) {
    profile(query: $query) {
      montages(first: $first, after: $after, status: $status) {
        nodes {
          id
          title
          game
          views
          createdAt
          thumbnailUrl(style: OVERLAY)
          url(orientation: LANDSCAPE)
          shareId
          type
          previewUrl
          trackId
          user {
            _id
          }
          internalId
          status
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

export const GET_ME = gql`
  query getMe {
    me {
      metadata {
        noEmailSet
        hasSharecodes
      }
      user {
        _id
        admin
        avatar

        google {
          active
        }

        userGroup {
          active
          type
          updated
          createdDate
          expires
          paymentDate
        }

        riot {
          puuid
          riotId
          active
          usedForSignUp
          region
        }
        discord {
          username
          id
          avatar
        }
        faceit {
          nickname
          player_id
          usedForSignUp
        }
        epic {
          usedForSignUp
          displayName
          id
        }
        battlenet {
          usedForSignUp
          battletag
        }
        tiktok {
          hasUploadScope
          display_name
          usedForSignUp
        }
        steam {
          steamid
          personaname
          active
          usedForSignUp
        }
        scope
        rank
        credits {
          providerName
          updated
          createdDate
        }
        discordSignUp
        emailVerified
        createdDate
        username
        ftue {
          hasCompleted
          isCS
          step
          stepHistory
          page
          skip {
            default
            profile
            games
            platforms
            steam
            sharecode
            socials
            download
            claim
          }
        }
      }
      stats {
        totalClips
        totalViews
      }
      profile {
        id
        ccpaPrivacyPreference
        profile {
          countryCode
          locale
          socials {
            twitch
            tiktok
            steam
            instagram
            twitter
            youtube
          }
          youtubeChannels
        }

        username
        createdDate
        appVersion
        gamersClub {
          enabled
          steamId64
        }
        settings {
          usernameChange
        }
        following
      }
    }
  }
`;

export const LOGIN = gql`
  query LoginQuery($email: String!, $password: String!) {
    authentication(email: $email, password: $password)
  }
`;

export const SIGNUP = gql`
  mutation Mutation($email: String!, $password: String!) {
    signup(email: $email, password: $password) {
      errors {
        code
        field
        message
      }
      success
      token
    }
  }
`;

export const USER_UPDATE = gql`
  mutation UserUpdate($input: UserUpdateInput!) {
    userUpdate(input: $input) {
      errors {
        code
        field
        message
      }
    }
  }
`;
